import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Dialog, DialogContent, DialogPortal } from 'components/ui/dialog'
import { Cdn } from 'utils/assets'
import {
  onboardingControllerFindOrCreateTutorialEndpoint,
  onboardingControllerUpdateTutorialEndpoint
} from 'api/onboarding-custom'
import { UpdateTutorialDtoAction } from 'api/generated/schemas'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import { OnboardingV3Context } from 'context/onboarding-v3'
import { useTranslation } from 'i18n'
import * as S from './ModalFinalOnboardingV3.styles'

const headerImage = Cdn.getImageUrl(
  'onboarding/onboarding-checklist-completed.webp'
)
const backgroundImage = Cdn.getImageUrl(
  'onboarding/fundo_feedback_final_sem_fundo.webp'
)

const MODAL_CLASS = 'modal-final-onboarding-v3'

type ModalFinalOnboardingV3Props = {
  isOpen: boolean
  onClose: () => void
}

const ModalFinalOnboardingV3 = ({
  isOpen,
  onClose
}: ModalFinalOnboardingV3Props) => {
  const { t } = useTranslation()
  const { user } = useUserInfo()
  const { registerModalOpen, registerModalClosed } =
    React.useContext(OnboardingV3Context)

  useEffect(() => {
    if (isOpen) {
      registerModalOpen()
      return () => registerModalClosed()
    }
  }, [isOpen, registerModalOpen, registerModalClosed])

  useEffect(() => {
    if (!isOpen || !user?.reader?.id) {
      return
    }

    const completeOnboarding = async () => {
      try {
        const createResponse =
          await onboardingControllerFindOrCreateTutorialEndpoint({
            name: 'checklist_onboarding'
          })

        if (createResponse.status === 201 && createResponse.data) {
          await onboardingControllerUpdateTutorialEndpoint({
            id: createResponse.data.id,
            action: UpdateTutorialDtoAction.COMPLETE
          })
          window.dispatchEvent(new CustomEvent('onboarding-completed'))
        }
      } catch (error) {
        if (error) {
          return
        }
      }
    }

    completeOnboarding()
  }, [isOpen, user?.reader?.id])

  const handleEntendi = () => {
    onClose()
  }

  const modalContainer = document.getElementById('modal')

  return (
    <>
      <S.ModalOverrides />
      {isOpen &&
        modalContainer &&
        createPortal(
          <>
            <S.BackgroundOverlay />
            <S.BackgroundImage src={backgroundImage} alt="" />
          </>,
          modalContainer
        )}
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogPortal container={modalContainer}>
          <DialogContent showCloseButton={false} className={MODAL_CLASS}>
            <S.Container>
              <S.CloseButton onClick={onClose} aria-label={t('Fechar')}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <title>{t('Fechar')}</title>
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </S.CloseButton>
              <S.HeaderIllustration>
                <S.HeaderImage
                  src={headerImage}
                  alt={t('Checklist Completo')}
                />
              </S.HeaderIllustration>

              <S.Content>
                <S.TextSection>
                  <S.Title>{t('Você completou todas as missões!')}</S.Title>
                  <S.Description>
                    {t(
                      'Parabéns! Continue lendo e descobrindo histórias para fazer sua floresta florescer cada vez mais.'
                    )}
                  </S.Description>
                </S.TextSection>
              </S.Content>

              <S.ButtonContainer>
                <S.PrimaryButton onClick={handleEntendi}>
                  {t('Entendi')}
                </S.PrimaryButton>
              </S.ButtonContainer>
            </S.Container>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </>
  )
}

export default ModalFinalOnboardingV3
